import React from 'react';
import { CreditDevelopmentClient } from '../../types';
import { StandardButton } from '../../components/standard';
import { useCreditDevelopmentClients } from '../../services/queries/creditManagementQueries';

interface CreditDevelopmentDashboardProps {
  onSelectClient: (client: CreditDevelopmentClient) => void;
  onBackToDashboard: () => void;
}

const CreditDevelopmentDashboard: React.FC<CreditDevelopmentDashboardProps> = ({ onSelectClient, onBackToDashboard }) => {
  const { data: clients = [] } = useCreditDevelopmentClients();
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center text-white text-lg">📈</div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Credit Development</h1>
              <p className="text-gray-600 dark:text-gray-400">Build credit history from no credit or limited credit background</p>
            </div>
          </div>
          <StandardButton
            variant="secondary"
            onClick={onBackToDashboard}
          >
            Back to Dashboard
          </StandardButton>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-green-900 dark:text-green-300">Active Clients</h3>
            <p className="text-2xl font-bold text-green-800 dark:text-green-400">{clients.length}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-blue-900 dark:text-blue-300">No Credit History</h3>
            <p className="text-2xl font-bold text-blue-800 dark:text-blue-400">1</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
            <h3 className="font-semibold text-purple-900 dark:text-purple-300">Limited Credit</h3>
            <p className="text-2xl font-bold text-purple-800 dark:text-purple-400">0</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credit History</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
              {clients.map((client) => (
                <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full font-medium bg-blue-100 text-blue-800">{client.creditHistory}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs rounded-full font-medium bg-green-100 text-green-800">{client.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {client.currentScore === 0 ? 'No Score' : client.currentScore}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => onSelectClient(client)}
                      className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
                    >
                      View Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreditDevelopmentDashboard;